/**
 * W1-11b (app-shell-ui): the shell's feature module (loaded first by loadModules.ts; ARCHITECTURE.md §6.1).
 *
 *  - commands and key bindings of shellCommands.ts (Appendix B: app.*, edit.undo|redo, view.*, playback.*, snap.*,
 *    nav.goto|reveal|prevKey|nextKey|prevNote|nextNote, help.palette|cheatSheet);
 *  - routes: the editor `#/p/:id/:workspace` (and `#/p/:id`, corrected to a workspace), the keyboard check page
 *    `#/dev/keys` and the effects comparer `#/dev/effects` (W3-01; loaded on demand, in every build: people run
 *    them on their own browsers for the checklist) and, in dev builds, the design-system gallery `#/dev/ui`;
 *  - the `@` palette provider (beats, times, markers);
 *  - `init`: keeps the audio transport on the editor store's rate, loop region (beats -> seconds through the tempo
 *    map, again after tempo edits) and metronome (beat clicks with bar accents); keeps the frame scheduler
 *    continuous while playing; and snaps the playhead to the grid on pause when settings.editing.snapPlayheadOnPause.
 */

import type { Unsubscribe } from '../../core/util/types';
import { Gallery } from '../../ui/Gallery';
import { lazyComponent } from '../registry/react';
import type { AppServices, FeatureModule, RouteDef } from '../registry/types';
import type { EditorStore } from '../../state/types';
import { EditorShell } from './EditorShell';
import { metronomeBeats, playheadBeat, seekBeat, snapNearest, tempoOf } from './playback';
import { SHELL_COMMANDS, SHELL_KEYBINDINGS } from './shellCommands';
import { useShell } from './ShellContext';
import { timePaletteProvider } from './timeProvider';

function EditorRoute(props: { readonly services: AppServices; readonly params: Readonly<Record<string, string>> }) {
  const { ui } = useShell();
  return <EditorShell services={props.services} ui={ui} params={props.params} />;
}

function GalleryRoute() {
  return <Gallery />;
}

const KeyMatrixRoute = lazyComponent<{ readonly services: AppServices }>(() =>
  import('./KeyMatrixPage').then((mod) => ({ default: mod.KeyMatrixPage })),
);

/** The effects capture comparer (W3-04 CR-1, Appendix C H5): framework-free DOM inside a React host. */
const EffectsDevRoute = lazyComponent<{ readonly services: AppServices; readonly params: Readonly<Record<string, string>> }>(
  () => import('./EffectsDevPage').then((mod) => ({ default: mod.EffectsDevPage })),
);

const ROUTES: readonly RouteDef[] = [
  { path: '#/p/:id/:workspace', component: EditorRoute },
  { path: '#/p/:id', component: EditorRoute },
  { path: '#/dev/keys', component: KeyMatrixRoute },
  { path: '#/dev/effects', component: EffectsDevRoute },
  ...(import.meta.env.DEV ? [{ path: '#/dev/ui', component: GalleryRoute }] : []),
];

/** Keeps the transport in line with the editor store and the document (see the module comment). */
export function connectTransport(s: AppServices): Unsubscribe {
  const offs: Unsubscribe[] = [];
  const transport = s.audio.transport;

  const applyLoop = (st: EditorStore): void => {
    const store = s.store;
    if (st.loop === null || store === null) {
      transport.setLoop(null);
      return;
    }
    const tempo = tempoOf(store);
    transport.setLoop({ start: tempo.secAt(st.loop.start), end: tempo.secAt(st.loop.end) });
  };
  const applyMetronome = (st: EditorStore): void => {
    const store = s.store;
    if (store === null || !st.metronome) s.audio.setMetronome(false, () => []);
    else s.audio.setMetronome(true, metronomeBeats(store));
  };
  const applyAll = (): void => {
    const st = s.editor.getState();
    if (transport.state.rate !== st.rate) transport.setRate(st.rate);
    applyLoop(st);
    applyMetronome(st);
  };

  offs.push(
    s.editor.subscribe((st, prev) => {
      if (st.rate !== prev.rate && transport.state.rate !== st.rate) transport.setRate(st.rate);
      if (st.loop !== prev.loop) applyLoop(st);
      if (st.metronome !== prev.metronome) applyMetronome(st);
    }),
  );

  // Tempo or meter edits move the loop's seconds and the metronome's clicks.
  let offStore: Unsubscribe | null = null;
  const watchStore = (): void => {
    offStore?.();
    offStore = null;
    const store = s.store;
    if (store !== null) {
      offStore = store.subscribe((cs) => {
        if (cs.dirty.tempo || cs.dirty.timing) {
          const st = s.editor.getState();
          applyLoop(st);
          applyMetronome(st);
        }
      });
    }
    applyAll();
  };
  watchStore();
  offs.push(s.onChange(watchStore));

  let wasPlaying = transport.state.playing;
  offs.push(
    transport.onChange((ts) => {
      s.scheduler.setContinuous(ts.playing);
      if (wasPlaying && !ts.playing && ts.mode !== 'holdReturn' && s.settings.value.editing.snapPlayheadOnPause) {
        const st = s.editor.getState();
        if (!st.returnOnStop && s.store !== null) seekBeat(s, snapNearest(playheadBeat(s), st.snap.div), false);
      }
      wasPlaying = ts.playing;
    }),
  );

  return () => {
    offStore?.();
    for (const off of offs) off();
  };
}

export default {
  id: 'shell',
  commands: SHELL_COMMANDS,
  keybindings: SHELL_KEYBINDINGS,
  routes: ROUTES,
  paletteProviders: (s) => [timePaletteProvider(s)],
  init: (s) => connectTransport(s),
} satisfies FeatureModule;
